import styled from "styled-components";

export const PanelHeader = styled.div`
  padding: 1.25rem 1.25rem 1rem;
  border-bottom: 1px solid #F3F4F6;
  flex-shrink: 0;
`;

export const PanelTitle = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.75rem 0;
`;

export const ProgressLabel = styled.div`
  font-size: 0.75rem;
  color: #6B7280;
  margin-bottom: 0.4rem;
`;

export const StepsBar = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 16px;
`;

export const StepsBarTrack = styled.div`
  position: absolute;
  left: 8px;
  right: 8px;
  top: 50%;
  height: 4px;
  background-color: #F3F4F6;
  border-radius: 999px;
  transform: translateY(-50%);
`;

export const StepsBarFill = styled.div<{ $percent: number }>`
  position: absolute;
  left: 8px;
  top: 50%;
  height: 4px;
  background-color: #021526;
  border-radius: 999px;
  transform: translateY(-50%);
  width: ${(props) => `calc((100% - 16px) * ${props.$percent / 100})`};
  transition: width 0.2s ease;
`;

export const StepBullet = styled.button<{ $state: "done" | "current" | "upcoming" }>`
  position: relative;
  z-index: 1;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${(props) => (props.$state === "upcoming" ? "#D1D5DB" : "#021526")};
  background-color: ${(props) => (props.$state === "upcoming" ? "#ffffff" : "#021526")};
  padding: 0;
  cursor: pointer;
  transition: transform 0.15s ease;

  &:hover {
    transform: scale(1.2);
  }
`;

// Custom tooltip instead of the native `title` attribute — browsers apply a
// fixed ~1s hover delay to `title` with no HTML/CSS way to shorten it.
export const StepTooltip = styled.span`
  position: absolute;
  top: 130%;
  left: 50%;
  transform: translateX(-50%);
  background-color: #111827;
  color: #ffffff;
  font-size: 0.6875rem;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.1s ease;
  z-index: 2;
`;

export const StepBulletWrapper = styled.div`
  position: relative;
  display: inline-flex;

  &:hover ${StepTooltip} {
    opacity: 1;
    transition-delay: 0.1s;
  }
`;

export const PanelBody = styled.div`
  padding: 1.25rem;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

export const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

export const FieldLabel = styled.label`
  font-size: 0.8125rem;
  font-weight: 600;
  color: #111827;
`;

export const FieldInput = styled.input`
  width: 100%;
  padding: 0.6rem 0.75rem;
  background-color: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #111827;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #021526;
    background-color: #ffffff;
  }
`;

export const FieldTextarea = styled.textarea`
  width: 100%;
  min-height: 4.5rem;
  padding: 0.6rem 0.75rem;
  background-color: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: #111827;
  resize: vertical;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #021526;
    background-color: #ffffff;
  }
`;

export const FieldHelpText = styled.p`
  font-size: 0.75rem;
  color: #6B7280;
  margin: 0;
  line-height: 1.4;
`;

export const BooleanToggleRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #111827;
  cursor: pointer;

  input {
    width: 1.1rem;
    height: 1.1rem;
    accent-color: #021526;
    cursor: pointer;
  }
`;

export const PanelFooter = styled.div`
  padding: 1rem 1.25rem;
  border-top: 1px solid #F3F4F6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-shrink: 0;
`;

export const SecondaryButton = styled.button`
  padding: 0.55rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid #E5E7EB;
  background-color: #ffffff;
  color: #4B5563;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: #F9FAFB;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

// Always mounted with a reserved min-width so toggling visibility via opacity
// never reflows the sibling buttons in PanelFooter (space-between would
// otherwise redistribute them when this element mounts/unmounts).
export const SavedIndicator = styled.span<{ $visible: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  color: #10B981;
  opacity: ${(props) => (props.$visible ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

export const AddressField = styled.div`
  position: relative;
`;

export const SuggestionsList = styled.ul`
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  right: 0;
  z-index: 10;
  max-height: 16rem;
  overflow-y: auto;
  margin: 0;
  padding: 0.25rem;
  list-style: none;
  background-color: #ffffff;
  border: 1px solid #E5E7EB;
  border-radius: 0.5rem;
  box-shadow: 0 8px 24px rgba(17, 24, 39, 0.08);
`;

export const SuggestionItem = styled.li<{ $highlighted: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.5rem 0.6rem;
  border-radius: 0.375rem;
  cursor: pointer;
  background-color: ${({ $highlighted }) => ($highlighted ? "#F3F4F6" : "transparent")};

  &:hover {
    background-color: #F3F4F6;
  }
`;

export const SuggestionAddress = styled.span`
  font-size: 0.875rem;
  color: #111827;
`;

export const SuggestionDetail = styled.span`
  font-size: 0.75rem;
  color: #6B7280;
`;
